import { useState } from 'react'
import { Button } from '.'
import { BellOff, Clock, Hash } from 'lucide-react'

interface MuteErrorModalProps {
  isOpen: boolean
  onClose: () => void
  errorMessage: string
  onMute: (options: { hours?: number; until_count?: number }) => Promise<void>
}

const HOUR_OPTIONS = [
  { value: 1, label: '1 hour' },
  { value: 4, label: '4 hours' },
  { value: 8, label: '8 hours' },
  { value: 24, label: '24 hours' },
  { value: 48, label: '48 hours' },
  { value: 72, label: '72 hours' },
]

const COUNT_OPTIONS = [
  { value: 10, label: '10 more occurrences' },
  { value: 50, label: '50 more occurrences' },
  { value: 100, label: '100 more occurrences' },
  { value: 500, label: '500 more occurrences' },
]

export function MuteErrorModal({ isOpen, onClose, errorMessage, onMute }: MuteErrorModalProps) {
  const [muteType, setMuteType] = useState<'hours' | 'count'>('hours')
  const [hours, setHours] = useState(24)
  const [count, setCount] = useState(100)
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      if (muteType === 'hours') {
        await onMute({ hours })
      } else {
        await onMute({ until_count: count })
      }
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 rounded-lg">
            <BellOff className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Mute Error</h2>
            <p className="text-sm text-slate-500">Temporarily silence notifications</p>
          </div>
        </div>

        <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-sm text-slate-700 font-medium truncate">{errorMessage}</p>
        </div>

        <div className="space-y-4 mb-6">
          {/* Mute type selection */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button
              onClick={() => setMuteType('hours')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                muteType === 'hours'
                  ? 'bg-accent-50 text-accent-700 border-r border-slate-200'
                  : 'bg-white text-slate-600 hover:bg-slate-50 border-r border-slate-200'
              }`}
            >
              <Clock className="w-4 h-4" />
              For a duration
            </button>
            <button
              onClick={() => setMuteType('count')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                muteType === 'count'
                  ? 'bg-accent-50 text-accent-700'
                  : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Hash className="w-4 h-4" />
              Until occurrences
            </button>
          </div>

          {/* Duration options */}
          {muteType === 'hours' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mute for</label>
              <div className="grid grid-cols-3 gap-2">
                {HOUR_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setHours(option.value)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      hours === option.value
                        ? 'bg-accent-50 border-accent-300 text-accent-700 font-medium'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Count options */}
          {muteType === 'count' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mute until</label>
              <div className="grid grid-cols-2 gap-2">
                {COUNT_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setCount(option.value)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      count === option.value
                        ? 'bg-accent-50 border-accent-300 text-accent-700 font-medium'
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center gap-2"
          >
            <BellOff className="w-4 h-4" />
            {isSubmitting ? 'Muting...' : 'Mute Error'}
          </Button>
        </div>
      </div>
    </div>
  )
}
